import type { FC, MouseEvent } from 'react';

import { QRCodeCanvas } from 'qrcode.react';
import { useMemo, useCallback } from 'react';

import { Box, Button, Container, Typography } from '@mui/material';

import { useTranslate } from 'src/locales';
import { useChannelContext } from 'src/pages/channel/hooks';
import { ChannelStepLayout } from 'src/pages/channel/shared/channel-step-layout';

import { toast } from 'src/components/snackbar';
import { Iconify } from 'src/components/iconify';

// ==================== Constants ====================
const LINE_COLORS = {
  primary: '#0c68e9',
  secondary: '#FFFFFF',
  text: '#333333',
  border: '#0c68e920',
  background: '#F5F5F5',
} as const;

const QR_CONFIG = {
  defaultSize: 200,
  level: 'H',
  minHeight: 300,
} as const;

// ==================== Types ====================
interface QRCodeDisplayProps {
  title?: string;
  subtitle?: string;
  qrUrl?: string | null;
  size?: number;
  redirectToRoot?: () => void;
  isDialog?: boolean;
}

// ==================== Component ====================
const QRCodeDisplay: FC<QRCodeDisplayProps> = ({
  subtitle = 'Add LINE Friends via QR Code',
  qrUrl = null,
  size = QR_CONFIG.defaultSize,
  isDialog = false,
  redirectToRoot,
}) => {
  const { t } = useTranslate('translation');
  const { state: channelState } = useChannelContext();

  const inviteLink = useMemo(
    () =>
      qrUrl ??
      channelState?.channelCreatedResponse?.config?.settings?.botInfo?.inviteLink ??
      channelState?.channelCreatedResponse?.config?.inviteLink,
    [
      qrUrl,
      channelState?.channelCreatedResponse?.config?.inviteLink,
      channelState?.channelCreatedResponse?.config?.settings?.botInfo?.inviteLink,
    ]
  );

  const handleCopy = useCallback(
    async (e: MouseEvent) => {
      e.stopPropagation();
      if (!inviteLink) return;

      try {
        await navigator.clipboard.writeText(inviteLink);
        toast.success(t('LINK_COPIED_SUCCESS'));
      } catch (error) {
        toast.error(t('LINK_COPY_FAILED'));
      }
    },
    [inviteLink, t]
  );

  const content = (
    <Container
      maxWidth="sm"
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        textAlign: 'center',
        p: 3,
      }}
    >
      <Typography variant="subtitle1" gutterBottom sx={{ mb: 4 }}>
        {subtitle}
      </Typography>

      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: QR_CONFIG.minHeight,
        }}
      >
        {inviteLink ? (
          <>
            <Box
              sx={{
                p: 2,
                bgcolor: LINE_COLORS.background,
                borderRadius: 2,
                mb: 3,
                border: `1px solid ${LINE_COLORS.border}`,
              }}
            >
              <QRCodeCanvas
                value={inviteLink}
                size={size}
                fgColor={LINE_COLORS.primary}
                bgColor="transparent"
                level={QR_CONFIG.level}
              />
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
              <Typography
                variant="body2"
                sx={{
                  wordBreak: 'break-all',
                  fontFamily: 'monospace',
                  fontWeight: 500,
                }}
              >
                {inviteLink}
              </Typography>

              <Iconify
                icon="solar:copy-bold"
                onClick={handleCopy}
                sx={{
                  cursor: 'pointer',
                  fontSize: 20,
                  color: LINE_COLORS.primary,
                  transition: 'opacity 0.2s',
                  '&:hover': { opacity: 0.7 },
                }}
              />
            </Box>
          </>
        ) : (
          <Typography variant="body2" color="error" sx={{ fontStyle: 'italic' }}>
            {t('NO_INVITE_LINK_AVAILABLE')}
          </Typography>
        )}

        {redirectToRoot && (
          <Button variant="contained" color="inherit" onClick={redirectToRoot} sx={{ mt: 2 }}>
            {t('DONE')}
          </Button>
        )}
      </Box>
    </Container>
  );

  if (isDialog) return content;

  if (redirectToRoot) {
    return <Box sx={{ pt: 5 }}>{content}</Box>;
  }

  return <ChannelStepLayout title="">{content}</ChannelStepLayout>;
};

export default QRCodeDisplay;
